import React from 'react';
import { Card, Badge, Button, Form } from 'react-bootstrap';
import { User, Mail, Phone, Pencil, Trash2, Shield } from 'lucide-react';
import { User as UserType } from '../../services/types';
import { useUserData } from '../../hooks';

interface UserCardProps {
    user: UserType;
    isAllowedLogin: boolean;
    onEdit: (user: UserType) => void;
    onDelete: (id: string) => void;
    onAllowLoginChange: (user: UserType, isChecked: boolean) => void;
}

const UserCard: React.FC<UserCardProps> = ({
    user,
    isAllowedLogin,
    onEdit,
    onDelete,
    onAllowLoginChange,
}) => {
    const { canEdit, canDelete } = useUserData();

    const getRoleBadgeVariant = (permissionId: number) => {
        switch (permissionId) {
            case 3:
                return 'danger';
            case 2:
                return 'warning';
            default:
                return 'secondary';
        }
    };

    const getRoleLabel = (permissionId: number) => {
        switch (permissionId) {
            case 3:
                return 'Super Admin';
            case 2:
                return 'Admin';
            default:
                return 'Normal User';
        }
    };

    return (
        <Card
            className="h-100 border-0 shadow-sm"
            style={{
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: canEdit ? 'pointer' : 'default',
            }}
            onMouseEnter={(e) => {
                if (canEdit) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
            }}
            onClick={() => canEdit && onEdit(user)}
        >
            <Card.Body>
                {/* Header with Role Badge */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#f0f0f0',
                                marginRight: '12px',
                            }}
                        >
                            <User size={24} style={{ color: '#6b7280' }} />
                        </div>
                        <div>
                            <h6 className="mb-1" style={{ fontWeight: 600, color: '#111827' }}>
                                {user.nome}
                            </h6>
                            <Badge bg={getRoleBadgeVariant(user.permission_id)} className="px-2 py-1">
                                <Shield size={12} className="me-1" />
                                {getRoleLabel(user.permission_id)}
                            </Badge>
                        </div>
                    </div>
                </div>

                {/* User Details */}
                <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                        <Mail size={16} className="me-2" style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>{user.email}</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <Phone size={16} className="me-2" style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>{user.num}</span>
                    </div>
                </div>

                {/* Allow Login Checkbox */}
                <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>
                            Allow Login
                        </span>
                        <Form.Check
                            type="checkbox"
                            checked={isAllowedLogin}
                            disabled={!canEdit}
                            onChange={(e) => {
                                e.stopPropagation();
                                onAllowLoginChange(user, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-2">
                    {canEdit && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="flex-fill"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(user);
                            }}
                        >
                            <Pencil size={14} className="me-1" />
                            Edit
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="flex-fill"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(user.public_id);
                            }}
                        >
                            <Trash2 size={14} className="me-1" />
                            Delete
                        </Button>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default UserCard;